import { Component, HostListener, OnInit } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { debounceTime, filter } from 'rxjs/operators';
import { cloneDeep, get, isArray, isEqual, isObject, set } from 'lodash-es';
import { environment } from '../environments/environment.active';
import { select, Store } from '@ngrx/store';
import { RootState } from './store/store.state';
import { AddAlert } from './alerts/store/alerts.action';
import { EventState, initialEventState } from './events/store/event.state';
import { selectEventsUi } from './events/store/events-list/event.selectors';
import { UpdateEventUIFromLocalStorage } from './events/store/events-list/event.actions';
import { LocalStorageService } from 'ngx-webstorage';
import {
    GlobalDomEventService,
    BaseAbstractComponent,
    AlertSeverity
} from 'quotient-ng-core';
import { selectAuthClaims } from './auth/store/auth.store';
import { NavigationStart, Router } from '@angular/router';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html'
})
export class AppComponent extends BaseAbstractComponent implements OnInit {
    private currentUIState;
    private initUIState;
    authenticateComplete: boolean = false;
    framed: boolean = false;
    eventsUi$: Observable<EventState['ui']>;

    constructor(
        private store: Store<RootState>,
        private globalDomEventService: GlobalDomEventService,
        private storage: LocalStorageService,
        private router: Router
    ) {
        super();
        this.eventsUi$ = store.pipe(select(selectEventsUi));
        /* To be notified once authentication is complete, so that header & footer component can be modified */
        this.store
            .pipe(
                select(selectAuthClaims),
                filter(
                    claims =>
                        isObject(claims) && isArray(claims.enabled_sso_apps)
                )
            )
            .subscribe(() => {
                this.authenticateComplete = true;
            });
        router.events.subscribe(event => {
            if (event instanceof NavigationStart) {
                if (event.url.indexOf('qc-hybrid') >= 0) {
                    this.framed = true;
                } else {
                    this.framed = false;
                }
            }
        });
    }

    ngOnInit() {
        console.log(
            'Quotient Merchandising loaded with environment settings',
            environment
        );

        this.eventsUi$
            .pipe(debounceTime(1000))
            .subscribe(eventUI =>
                this.storage.store('currentUIState', eventUI)
            );

        this.currentUIState = this.storage.retrieve('currentUIState');
        this.initUIState = this.storage.retrieve('initUIState');

        if (
            this.currentUIState !== undefined &&
            this.currentUIState !== null &&
            this.initUIState !== undefined &&
            this.initUIState !== null
        ) {
            this.store.dispatch(
                new UpdateEventUIFromLocalStorage(
                    this.updateStateFromLocalStorage(
                        cloneDeep(initialEventState.ui),
                        this.initUIState,
                        this.currentUIState,
                        [
                            'eventSearchCriteria.basicFilters',
                            'eventSearchCriteria.advancedFilters',
                            'eventGrid.pagination.pageSize'
                        ]
                    )
                )
            );
        }

        this.storage.store('initUIState', initialEventState.ui);
    }

    /**
     * This function returns an updated version of the given state, by updating it with the data from
     * the stored state (could be from browser's local storage). The updates are done only to the
     * paths mentioned in the storedPropertyList
     * @param currentStateSchema: object which has to be modified and returned
     * @param storedStateSchema: object which is a copy of the state from a previous version of app.
     * @param storedState: object that will be used to update the given state.
     * @param storedPropertyList: A list of dot separated strings, that represent a path to a property in the state.
     */
    updateStateFromLocalStorage<T extends object>(
        currentStateSchema: T,
        storedStateSchema: any,
        storedState: any,
        storedPropertyList: string[]
    ): T {
        let alertShown = false;
        storedPropertyList.forEach((path: string) => {
            if (
                get(storedStateSchema, path) !== undefined &&
                isEqual(
                    get(currentStateSchema, path),
                    get(storedStateSchema, path)
                )
            ) {
                set(currentStateSchema, path, get(storedState, path));
            } else if (!alertShown) {
                alertShown = true;
                this.store.dispatch(
                    new AddAlert({
                        message: 'Some of the filter settings have been reset',
                        severity: AlertSeverity.INFO
                    })
                );
            }
        });
        return currentStateSchema;
    }

    @HostListener('document:click', ['$event'])
    public onDocumentClick(event: MouseEvent): void {
        this.globalDomEventService.documentClickSubject.next(event);
    }

    preOnDestroy() {}
}
